"use client";

import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

interface PatternViewerProps {
  patternName: 'cell-pattern' | 'tissue-grid' | 'molecular-network' | 'fluorescence';
  className?: string;
  variant?: 'default' | 'light' | 'highlight';
  usage?: 'background' | 'card' | 'header' | 'loading' | 'decoration';
  size?: 'sm' | 'md' | 'lg' | 'full';
  opacity?: number;
  animated?: boolean;
}

// A component that displays SVG patterns
export function PatternViewer({
  patternName,
  className,
  variant = 'default',
  usage = 'decoration',
  size = 'md',
  opacity,
  animated = false
}: PatternViewerProps) {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Size class mappings
  const sizeClasses = {
    'sm': 'w-32 h-32',
    'md': 'w-64 h-64',
    'lg': 'w-96 h-96',
    'full': 'w-full h-full',
  };

  // Usage-specific class mappings
  const usageClasses = {
    'background': 'absolute inset-0 -z-10',
    'card': 'absolute inset-0 -z-10 rounded-lg overflow-hidden',
    'header': 'absolute inset-0 -z-10 opacity-[0.15]',
    'loading': 'mx-auto my-8',
    'decoration': 'flex justify-center items-center',
  };

  // Variant-specific class mappings (applied to the SVG content via inlineStyles)
  const variantStyles = {
    'default': {},
    'light': { opacity: 0.15, filter: 'brightness(1.1)' },
    'highlight': { opacity: 0.25, filter: 'brightness(1.2) saturate(1.2)' },
  };

  // Animation classes (only apply when animated=true)
  const animationClass = animated 
    ? patternName === 'molecular-network' 
      ? 'animate-pulse' 
      : patternName === 'fluorescence' 
        ? 'animate-glow' 
        : 'animate-subtle-float' 
    : '';

  // Apply variant-specific styles directly to the SVG 
  const applyVariantStyles = (svgString: string, variant: string): string => {
    // Extract the background rectangle to apply variant styles
    if (variant === 'default') return svgString;
    
    // For light variant, modify background opacity
    if (variant === 'light') {
      return svgString
        .replace(/fill-opacity="(0\.\d+)"/g, (match, p1) => `fill-opacity="${Math.min(parseFloat(p1) * 0.6, 1).toFixed(2)}"`)
        .replace(/opacity="(0\.\d+)"/g, (match, p1) => `opacity="${Math.min(parseFloat(p1) * 0.6, 1).toFixed(2)}"`);
    }
    
    // For highlight variant, increase saturation by modifying colors
    if (variant === 'highlight') {
      return svgString
        .replace(/fill="#([0-9A-F]{6})"/gi, (match, p1) => {
          // Make colors more vibrant for highlight variant
          return `fill="#${p1}" class="highlight-color"`;
        })
        .replace(/<svg/, '<svg class="highlight-svg"');
    }
    
    return svgString;
  };

  // Update SVG viewBox and preserveAspectRatio for better scaling and positioning
  const optimizeSvgForViewing = (svgString: string): string => {
    // Add preserveAspectRatio attribute if not present to ensure the SVG scales correctly
    if (!svgString.includes('preserveAspectRatio')) {
      return svgString.replace(/<svg/, '<svg preserveAspectRatio="xMidYMid meet"');
    }
    return svgString;
  };

  // Fetch SVG content based on pattern name
  useEffect(() => {
    const fetchSvg = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/patterns/${patternName}`);
        if (!response.ok) {
          throw new Error(`Failed to load pattern: ${response.status}`);
        }
        const svgString = await response.text();
        // Apply variant styles and optimize for viewing
        const processedSvg = optimizeSvgForViewing(applyVariantStyles(svgString, variant));
        setSvgContent(processedSvg);
        setError(null);
      } catch (err) {
        console.error('Error loading SVG pattern:', err);
        setError('Failed to load pattern');
        
        // Fallback to a placeholder with the pattern name
        setSvgContent(`<svg width="100%" height="100%" viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg">
          <rect width="800" height="800" fill="#F8F9FA" />
          <text x="400" y="400" text-anchor="middle" dominant-baseline="middle" font-family="system-ui" font-size="24" fill="#ADB5BD">
            ${patternName}
          </text>
        </svg>`);
      } finally {
        setLoading(false);
      }
    };

    fetchSvg();
  }, [patternName, variant]);

  const combinedClassName = cn(
    'pattern-viewer',
    `pattern-${patternName}`,
    sizeClasses[size],
    usageClasses[usage],
    animationClass,
    className
  );

  // Apply inline opacity if specified
  const style = opacity !== undefined ? { opacity: opacity / 100 } : {};

  // Special case: use PNG for cell-pattern in loading usage
  if (patternName === 'cell-pattern' && usage === 'loading') {
    return (
      <div className={cn('pattern-viewer flex items-center justify-center', sizeClasses[size], className)} style={style}>
        <img src="/cell-pattern.png" alt="Cell Pattern" className="w-full h-full object-contain animate-pulse" />
      </div>
    );
  }

  return (
    <div className={combinedClassName} style={style}>
      {loading ? (
        <div className="flex items-center justify-center w-full h-full bg-gray-100 animate-pulse rounded">
          <p className="text-sm text-gray-500">Loading pattern...</p>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center w-full h-full bg-gray-100 rounded">
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      ) : (
        <div 
          className="w-full h-full flex items-center justify-center"
          dangerouslySetInnerHTML={{ __html: svgContent || '' }} 
        />
      )}
    </div>
  );
}

// Convenience wrapper for using patterns as backgrounds
export function PatternBackground({
  patternName,
  variant = 'light',
  children,
  className,
  ...props
}: React.PropsWithChildren<Omit<PatternViewerProps, 'usage' | 'size'> & { patternName: PatternViewerProps['patternName'] }>) {
  return (
    <div className={cn('relative', className)}>
      <PatternViewer 
        patternName={patternName} 
        variant={variant} 
        usage="background" 
        size="full" 
        {...props} 
      />
      {children}
    </div>
  );
}

// Convenience wrapper for using patterns in card backgrounds
export function CardPattern({
  patternName,
  variant = 'light',
  children,
  className,
  ...props
}: React.PropsWithChildren<Omit<PatternViewerProps, 'usage' | 'size'> & { patternName: PatternViewerProps['patternName'] }>) {
  return (
    <div className={cn('relative rounded-lg overflow-hidden', className)}>
      <PatternViewer 
        patternName={patternName} 
        variant={variant} 
        usage="card" 
        size="full" 
        {...props} 
      />
      {children}
    </div>
  );
}

// Pattern-based loading indicator
export function LoadingPattern({
  patternName = 'molecular-network',
  size = 'md',
  className,
  ...props
}: Omit<PatternViewerProps, 'usage' | 'animated'> & { patternName?: PatternViewerProps['patternName'] }) {
  return (
    <PatternViewer 
      patternName={patternName} 
      usage="loading" 
      size={size} 
      animated={true}
      className={cn('animate-pulse', className)}
      {...props} 
    />
  );
} 
